/**
 * Distance from a known-size square marker (e.g. printed 50mm black square).
 * Paper-fill estimation is unreliable — this is much more accurate.
 */

import { ASSUMED_HFOV_DEG } from './distance';

export const MARKER_SIZE_MM = 50;

function luminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Find the best square dark marker in the frame.
 * Returns { sidePx, cx, cy, score } in full video pixels, or null.
 */
export function findSquareMarkerPx(video, sampleMax = 360) {
  if (!video || !video.videoWidth) return null;

  const scale = Math.min(1, sampleMax / video.videoWidth);
  const w = Math.max(48, Math.round(video.videoWidth * scale));
  const h = Math.max(48, Math.round(video.videoHeight * scale));

  const canvas = findSquareMarkerPx._canvas || document.createElement('canvas');
  findSquareMarkerPx._canvas = canvas;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(video, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  let bgSum = 0;
  let bgCount = 0;
  for (let y = 0; y < h; y += 4) {
    for (let x = 0; x < w; x += 4) {
      const i = (y * w + x) * 4;
      bgSum += luminance(data[i], data[i + 1], data[i + 2]);
      bgCount += 1;
    }
  }
  const bgMean = bgCount ? bgSum / bgCount : 160;
  const cut = Math.min(bgMean * 0.42, bgMean - 45);

  const mask = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      const yv = luminance(data[i], data[i + 1], data[i + 2]);
      mask[y * w + x] = yv < cut ? 1 : 0;
    }
  }

  const visited = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let best = null;

  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const start = y * w + x;
      if (!mask[start] || visited[start]) continue;

      let top = 0;
      stack[top++] = start;
      visited[start] = 1;
      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      let area = 0;
      let sumX = 0;
      let sumY = 0;

      while (top > 0) {
        const p = stack[--top];
        const px = p % w;
        const py = (p / w) | 0;
        area += 1;
        sumX += px;
        sumY += py;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
        const nbs = [p - 1, p + 1, p - w, p + w];
        for (let n = 0; n < 4; n += 1) {
          const np = nbs[n];
          if (np < 0 || np >= mask.length) continue;
          const nx = np % w;
          const ny = (np / w) | 0;
          if (Math.abs(nx - px) + Math.abs(ny - py) !== 1) continue;
          if (!mask[np] || visited[np]) continue;
          visited[np] = 1;
          stack[top++] = np;
        }
      }

      const bw = maxX - minX + 1;
      const bh = maxY - minY + 1;
      if (bw < w * 0.06 || bh < h * 0.06) continue;
      if (bw > w * 0.55 || bh > h * 0.55) continue;

      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      if (aspect > 1.25) continue;

      const compactness = area / (bw * bh);
      if (compactness < 0.55) continue;

      // Prefer more square + larger + filled
      const squareScore = 1 / aspect;
      const sizeScore = Math.min(bw, bh) / Math.min(w, h);
      const score = squareScore * 2 + compactness * 2 + sizeScore;

      if (!best || score > best.score) {
        best = {
          sidePx: ((bw + bh) / 2 / scale),
          cx: (sumX / area) / scale,
          cy: (sumY / area) / scale,
          score,
        };
      }
    }
  }

  return best;
}

export function distanceFromMarkerPx({
  markerSidePx,
  imageWidthPx,
  markerSizeMm = MARKER_SIZE_MM,
  hfovDeg = ASSUMED_HFOV_DEG,
}) {
  if (!markerSidePx || !imageWidthPx) return null;
  const hfovRad = (hfovDeg * Math.PI) / 180;
  const distanceMm =
    markerSizeMm /
    (2 * (markerSidePx / imageWidthPx) * Math.tan(hfovRad / 2));
  const cm = distanceMm / 10;
  if (cm < 3 || cm > 80) return null;
  return cm;
}

export function measureMarkerDistance(video) {
  const marker = findSquareMarkerPx(video);
  if (!marker || !video?.videoWidth) {
    return { distanceCm: null, marker: null };
  }
  const distanceCm = distanceFromMarkerPx({
    markerSidePx: marker.sidePx,
    imageWidthPx: video.videoWidth,
  });
  return { distanceCm, marker };
}
